import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useQuery, useMutation, useReactiveVar, useLazyQuery } from '@apollo/client';
import { Button, Divider, Col, Row, notification, message } from 'antd';
import { useIntercom } from 'react-use-intercom';

import { CONTENT_LIBRARY_ADD_CHILD } from 'graphql/mutations/contentLibraryMutations';
import { GET_TREEVIEW_DATA } from 'graphql/queries/contentLibraryQueries';
import { getUserId, sectionsVar } from 'graphql/cache';
import { GET_ALL_SECTIONS_LIBRARIES } from 'graphql/queries/contentLibraryQueries';
import { SECTIONS_LIMIT } from 'constants/index';

import FolderIcon from 'components/Icons/FolderIcon';
import RightIcon from 'components/Icons/RightIcon';
import AddIcon from 'components/Icons/AddIcon';
import AddFolder from 'components/Icons/AddFolder';
import UploadContentLibraryItem from 'components/UploadContentLibraryItem/UploadContentLibraryItem';
import Prompt from 'components/Prompt';

import './LibraryItems.scss';

const LibraryItems = ({
  onBackClick,
  ChildComponent,
  type,
  coversRef,
  coverType,
  saveProposal,
}) => {
  const [selectedFolder, setSelectedFolder] = useState('');
  const [currentFolderData, setCurrentFolderData] = useState([]);
  const [path, setPath] = useState();
  const [showUploadModal, showOrHidesUploadModal] = useState(false);
  const [showOrHidesNewFolderModal, setShowOrHidesNewFolderModal] = useState(false);
  const [isLoadingMoreSections, setIsLoadingMoreSections] = useState(false);

  const allSectionCursorRef = useRef(null);
  const isLoadingMoreRef = useRef(false);
  const sections = useReactiveVar(sectionsVar || []);

  /**
   * Process fetched data
   *
   * @param {Object} data
   * @param {boolean} append
   */
  const processFetchedData = useCallback(
    (data, append = false) => {
      if (!data?.fetchContentLibrarySectionsItems) {
        return;
      }

      const { sections: newSections, pagingMetadata } = data.fetchContentLibrarySectionsItems;

      if (!newSections) {
        return;
      }

      setIsLoadingMoreSections(false);
      isLoadingMoreRef.current = false;

      if (append) {
        const oldSections = sections || [];

        // remove duplicates
        const existingIds = new Set(oldSections.map((item) => item._id));
        const filteredNewSections = [];

        for (const item of newSections) {
          if (!existingIds.has(item._id)) {
            existingIds.add(item._id);
            filteredNewSections.push(item);
          }
        }

        sectionsVar([...oldSections, ...filteredNewSections]);

        if (filteredNewSections.length > 0) {
          allSectionCursorRef.current = pagingMetadata;
        }
      } else {
        sectionsVar(newSections);
        allSectionCursorRef.current = pagingMetadata;
      }
    },
    [sections]
  );

  /**
   * Fetch all sections
   */
  const [fetchAllSections, { refetch: refetchSections, fetchMore }] = useLazyQuery(
    GET_ALL_SECTIONS_LIBRARIES,
    {
      fetchPolicy: 'network-only',
      onCompleted: (data) => processFetchedData(data),
      onError: () => {
        message.error('Error fetching sections from library');
      },
    }
  );

  /**
   * Use effect
   */
  useEffect(() => {
    if (type === 'sections' || type === 'headers') {
      fetchAllSections({
        variables: {
          limit: SECTIONS_LIMIT * (window.innerHeight >= 1250 ? 2 : 1),
          cursorPaging: null,
          search: '',
          key: selectedFolder || (type === 'headers' ? 'headers' : ''),
          type,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, selectedFolder]);

  /**
   * Fetch more sections
   */
  const fetchMoreSections = useCallback(async () => {
    const allSectionCursor = allSectionCursorRef.current;

    if (!allSectionCursor?.hasNext || isLoadingMoreRef.current) {
      return;
    }

    isLoadingMoreRef.current = true;
    setIsLoadingMoreSections(true);

    const { data } = await fetchMore({
      variables: {
        limit: SECTIONS_LIMIT * (window.innerHeight >= 1250 ? 2 : 1),
        cursorPaging: allSectionCursor.cursor,
        search: '',
        key: selectedFolder || (type === 'headers' ? 'headers' : ''),
        type,
      },
    });

    processFetchedData(data, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFolder, processFetchedData]);

  /**
   * Use effect
   */
  useEffect(() => {
    const mainContent = document.querySelector('.ant-tabs-content-holder');
    if (!mainContent) {
      return;
    }
    async function onScroll() {
      if (isLoadingMoreRef.current) {
        return;
      }
      if (mainContent?.scrollTop + mainContent?.offsetHeight + 1 > mainContent?.scrollHeight) {
        fetchMoreSections();
      }
    }

    mainContent.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      mainContent.removeEventListener('scroll', onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchMoreSections]);

  const { trackEvent } = useIntercom();

  const { data, refetch } = useQuery(GET_TREEVIEW_DATA, {
    variables: {
      type: type === 'media' ? 'medias' : type,
    },
  });

  const [addChild] = useMutation(CONTENT_LIBRARY_ADD_CHILD, {
    onCompleted() {
      setShowOrHidesNewFolderModal(false);
      notification.success({
        description: 'Created new folder successfully.',
      });
      refetch({
        variables: {
          type: type === 'media' ? 'medias' : type,
        },
      });
    },
    onError: () => {
      notification.error({
        description: 'Created new folder failed.',
      });
    },
  });

  useEffect(() => {
    (async () => {
      if (data && data.fetchContentLibrary) {
        if (selectedFolder) {
          const folders = [];
          let key = '';
          setCurrentFolderData(
            selectedFolder.split('.').reduce((o, k) => {
              key = `${key ? `${key}.` : ''}${k}`;
              const child = o && o[k];
              if (child && child.title)
                folders.push({
                  title: child.title,
                  key: key,
                });
              return child;
            }, data.fetchContentLibrary)?.children || []
          );
          setPath(folders);
        } else {
          setPath(null);
          setCurrentFolderData(data.fetchContentLibrary.children);
        }
      } else {
        setCurrentFolderData([]);
      }
    })();
  }, [selectedFolder, data, setCurrentFolderData]);

  return (
    <div className="covers-library-section">
      <div>
        <Row className="folder-path">
          <Col className="path">
            {path ? (
              [{ title: type }, ...path].map((p, i) => (
                <>
                  {!!i && <span key={p.key + 'span'}>/</span>}
                  <span
                    className="folder-path-name"
                    key={p.key}
                    onClick={() => setSelectedFolder(p.key)}>
                    {p.title}
                  </span>
                </>
              ))
            ) : (
              <span className="folder-path-name">{type}</span>
            )}
          </Col>
          <Col
            className="folder-path-buttons"
            style={
              type?.toLowerCase() === 'covers' || type?.toLowerCase() === 'media'
                ? { display: 'flex', justifyContent: 'flex-end', flex: 2 }
                : {}
            }>
            {(type?.toLowerCase() === 'covers' || type?.toLowerCase() === 'media') && (
              <Button
                className="medium-btn secondary-btn icon-right"
                icon={<AddIcon />}
                onClick={() => showOrHidesUploadModal(true)}>
                UPLOAD
              </Button>
            )}
            <Button
              className="medium-btn grey-btn back-button"
              onClick={() =>
                selectedFolder
                  ? setSelectedFolder(selectedFolder.split('.').slice(0, -2).join('.'))
                  : onBackClick()
              }>
              BACK
            </Button>
          </Col>
        </Row>
      </div>
      <Divider />
      {currentFolderData &&
        currentFolderData.map((folder, i) => {
          return folder.title ? (
            <div
              key={i}
              className="folder"
              onClick={() =>
                setSelectedFolder(`${selectedFolder ? `${selectedFolder}.` : ''}children.${i}`)
              }>
              <div>
                <FolderIcon />
                {folder.title}
              </div>
              <RightIcon className="folder-path-arrow" />
            </div>
          ) : (
            <></>
          );
        })}

      <Row>
        <Button
          className="large-btn grey-btn prosper-button-add-folder-icon"
          icon={<AddFolder />}
          size="large"
          block
          onClick={() => setShowOrHidesNewFolderModal(true)}>
          New Folder
        </Button>
      </Row>

      <Divider />

      {ChildComponent({
        selectedFolder,
        type,
        refetchSections,
        isLoadingMoreSections,
        sections,
        fetchMoreSections,
        saveProposal,
        coversRef,
        processFetchedData,
      })}

      <UploadContentLibraryItem
        type={type}
        selectedFolderKey={selectedFolder}
        showUploadModal={showUploadModal}
        cancelUploadModal={() => showOrHidesUploadModal(false)}
        openUploadModal={() => showOrHidesUploadModal(true)}
        selectedCoverType={coverType}
        onSuccess={async (urls) => {
          trackEvent('click-upload-cover');
          if (urls?.length && type === 'covers') {
            saveProposal({ 'draft.header.cover': urls[0].url });
          }
          if (coversRef && coversRef.current) {
            await coversRef.current.reloadItems();
          }
        }}
        multiSelect={false}
      />

      {showOrHidesNewFolderModal && (
        <Prompt
          modalProps={{ centered: true }}
          title="Enter Folder Name"
          visible={showOrHidesNewFolderModal}
          cancelText="CANCEL"
          confirmText="ADD"
          handleCancel={() => setShowOrHidesNewFolderModal(false)}
          handleConfirm={(title) => {
            if (title) {
              addChild({
                variables: {
                  child: { title, uid: getUserId() },
                  key: `${selectedFolder}${selectedFolder ? '.' : ''}children`,
                  id: data?.fetchContentLibrary?._id,
                },
              });
            }
          }}
        />
      )}
    </div>
  );
};

export default LibraryItems;
